import { useRef, useState, useEffect } from 'preact/hooks';
import { RefObject } from 'preact';
import emitter from '@webcore/core/events/eventemitter';

// ============================================================================
// HOOK TYPES
// ============================================================================

export type ViewerEventType = 'loading' | 'loaded' | 'error';

// ============================================================================
// HOOK RETURN TYPE
// ============================================================================
export interface UsePreviewReturn {
  /** Ref to attach to the canvas element */
  canvasRef: RefObject<HTMLCanvasElement>;
  /** Whether the model is currently loading */
  isLoading: boolean;
  /** Whether the model has finished loading */
  isLoaded: boolean;
  /** Error if model failed to load */
  error: Error | null;
  /** Whether canvas is in fullscreen mode */
  isFullscreen: boolean;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================
/**
 * Hook for 3D model preview on a canvas.
 * 
 * @param contentId - The content ID to display
 * 
 * The hook registers the canvas with main.app.ts which handles:
 * - Config lookup from ContentData
 * - Lazy loading (attach when visible, detach when hidden)
 * - Fullscreen functionality
 */
export function usePreview(contentId: string): UsePreviewReturn {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Register canvas on mount, unregister on unmount
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    (emitter as any).emit('canvas-mount', { canvas, contentId });
    
    return () => {
      (emitter as any).emit('canvas-unmount', { canvas });
    };
  }, [contentId]);
  
  // Listen for viewer events (loading/loaded/error)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const handleViewerEvent = (event: { canvas: HTMLCanvasElement; type: ViewerEventType; error?: Error }) => {
      if (event.canvas !== canvas) return;
      
      switch (event.type) {
        case 'loading':
          setIsLoading(true);
          setIsLoaded(false);
          setError(null);
          break;
        case 'loaded':
          setIsLoading(false);
          setIsLoaded(true);
          break;
        case 'error':
          setIsLoading(false);
          setIsLoaded(false);
          setError(event.error ?? new Error('Unknown error'));
          break;
      }
    };
    
    (emitter as any).on('preview-event', handleViewerEvent);
    
    return () => {
      (emitter as any).off('preview-event', handleViewerEvent);
    };
  }, []);
  
  // Listen for fullscreen state changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const handleFullscreenEvent = (event: { canvas: HTMLCanvasElement; isFullscreen: boolean }) => {
      if (event.canvas !== canvas) return;
      setIsFullscreen(event.isFullscreen);
    };
    
    (emitter as any).on('preview-fullscreen', handleFullscreenEvent);
    
    return () => {
      (emitter as any).off('preview-fullscreen', handleFullscreenEvent);
    };
  }, []);

  return {
    canvasRef,
    isLoading,
    isLoaded,
    error,
    isFullscreen,
  };
}
